import { useEffect, useState } from "react";

import debug from "debug";

import { GET_LOCATION_BY_IP_URL } from "../constants";

import type { CountriesApiResponse, IpAddress } from "./types/CountrySelector.types";

import { appendQueryString, getMarketUrlKey } from "./redirectSource";
import { useSearchParamsConfig } from "./useSearchParamsConfig";

const log = debug("desjti:country-selector:useGeoAndRedirect");

/**
 * Hook for loading address, enabled countries, and performing auto-redirect.
 */
export const useGeoAndRedirect = (
  countriesApi: string,
  editor: boolean,
  staticMode = false
): {
  address: IpAddress;
  loading: boolean;
  enabledData: CountriesApiResponse;
  noGeo?: boolean;
} => {
  const [address, setAddress] = useState<IpAddress>({
    ipAddress: "",
    countryRegion: { isoCode: "" },
  });
  const [enabledData, setEnabledData] = useState<CountriesApiResponse>([]);
  const [loading, setLoading] = useState(!editor || !staticMode);

  const { search: queryString, isSearchParamLoaded, bypass, activeSource, noGeo } = useSearchParamsConfig();

  useEffect(() => {
    if (editor || staticMode) {
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      if (!isSearchParamLoaded) {
        return;
      }

      try {
        const [addrRes, countriesRes] = await Promise.all([
          fetch(GET_LOCATION_BY_IP_URL).then((r) => r.json()),
          fetch(countriesApi).then((r) => r.json()),
        ]);

        let addr: IpAddress = addrRes;
        if (bypass) {
          addr = {
            ...addr,
            countryRegion: { isoCode: bypass },
          };
        }
        setAddress(addr);

        const dataArray: CountriesApiResponse = countriesRes[0]?.data || [];
        setEnabledData(dataArray);

        if (!noGeo && addr.countryRegion.isoCode) {
          const key = getMarketUrlKey(activeSource);
          const foundItem = dataArray.find((e) => e.code === addr.countryRegion.isoCode);
          if (foundItem?.data[key]) {
            window.location.href = appendQueryString(foundItem.data[key], queryString);
            return;
          }
        }
      } catch (e) {
        log("Error in useGeoAndRedirect", e);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [countriesApi, editor, staticMode, bypass, activeSource, noGeo, isSearchParamLoaded, queryString]);

  return { address, loading, enabledData, noGeo };
};
